import React, { useEffect, useMemo, useRef, useState } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useUnsavedChanges } from "./UnsavedChangesContext";

function normalizePhone(value) {
  const raw = (value || "").toString().trim();
  if (!raw) return "";
  const parsed = parsePhoneNumberFromString(raw, "US");
  if (parsed && parsed.isValid()) return parsed.number;
  const digits = raw.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
}

function FamilyForm({ family, onChange, onSave, saving, onDuplicate }) {
  if (!family) return null;

  function update(field, value) {
    onChange({ ...family, [field]: value });
  }

  function renderSkipRow(idx) {
    const dateKey = `skipDate${idx}`;
    const slotKey = `skipSlot${idx}`;
    const textKey = `skipText${idx}`;
    return (
      <div style={{ display: "grid", gridTemplateColumns: "160px 120px 1fr", gap: 10, alignItems: "center" }}>
        <input
          type="date"
          value={family[dateKey] || ""}
          onChange={(e) => update(dateKey, e.target.value)}
        />
        <select value={family[slotKey] || ""} onChange={(e) => update(slotKey, e.target.value)}>
          <option value="">AM/PM…</option>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
        <input
          placeholder="Skip activity text (optional)"
          value={family[textKey] || ""}
          onChange={(e) => update(textKey, e.target.value)}
        />
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 16, background: "var(--card)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ margin: 0 }}>{family.name || "Family/Group"}</h2>
          <button onClick={onDuplicate} style={{ fontSize: 12 }}>
            Duplicate
          </button>
        </div>
        <button onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 700 }}>Name</div>
          <input value={family.name || ""} onChange={(e) => update("name", e.target.value)} />
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 700 }}>VIP Family/Group</div>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!family.vip}
              onChange={(e) => update("vip", e.target.checked)}
            />
            <span style={{ fontSize: 12, opacity: 0.8 }}>Only schedule within this family</span>
          </label>
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 700 }}>Max Choices</div>
          <input
            type="number"
            min="1"
            max="10"
            value={family.maxChoices ?? ""}
            onChange={(e) => update("maxChoices", e.target.value)}
          />
        </div>
        <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 700 }}>Arrival Date</div>
            <input
              type="date"
              value={family.arrivalDate || ""}
              onChange={(e) => update("arrivalDate", e.target.value)}
              onBlur={() => {
                if (!family.departureDate && family.arrivalDate) {
                  update("departureDate", family.arrivalDate);
                }
              }}
            />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 700 }}>Departure Date</div>
            <input
              type="date"
              value={family.departureDate || ""}
              onChange={(e) => update("departureDate", e.target.value)}
            />
          </div>
        </div>
        <div style={{ display: "grid", gap: 6, gridColumn: "1 / -1" }}>
          <div style={{ fontWeight: 700 }}>Information Text</div>
          <textarea
            rows={3}
            value={family.infoText || ""}
            onChange={(e) => update("infoText", e.target.value)}
            placeholder="Shows on the guest choices page."
          />
        </div>
        <div style={{ display: "grid", gap: 6, gridColumn: "1 / -1" }}>
          <div style={{ fontWeight: 700 }}>Itinerary Message</div>
          <textarea
            rows={3}
            value={family.itineraryMessage || ""}
            onChange={(e) => update("itineraryMessage", e.target.value)}
            placeholder="Shows at the top of the family itinerary link."
          />
        </div>
      </div>

      <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Skip activity slots</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Leave blank if no skip is needed. Text is optional and overrides the default label.
        </div>
        {renderSkipRow(1)}
        {renderSkipRow(2)}
        {renderSkipRow(3)}
      </div>
    </div>
  );
}

export default function FamiliesPage() {
  const { setUnsavedChanges, requestConfirm } = useUnsavedChanges();
  const [families, setFamilies] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const originalFamilyRef = useRef(null);
  const [creatingGuest, setCreatingGuest] = useState(false);
  const [newGuestName, setNewGuestName] = useState("");
  const [newGuestEmail, setNewGuestEmail] = useState("");
  const [newGuestPhone, setNewGuestPhone] = useState("");
  const [newGuestLevel, setNewGuestLevel] = useState("");
  const [newGuestArrival, setNewGuestArrival] = useState("");
  const [newGuestDeparture, setNewGuestDeparture] = useState("");
  const [addGuestOpen, setAddGuestOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [uploadingPackingList, setUploadingPackingList] = useState(false);
  const [deletingPackingListId, setDeletingPackingListId] = useState(null);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [familyGuests, setFamilyGuests] = useState([]);
  const [familyGuestsLoading, setFamilyGuestsLoading] = useState(false);
  const [expandedGuests, setExpandedGuests] = useState(() => new Set());
  const [guestRequestDetails, setGuestRequestDetails] = useState({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch("/api/families?includeDetails=1");
        const json = await res.json();
        if (!cancelled) {
          const list = Array.isArray(json) ? json : [];
          setFamilies(list);
          if (!selectedId && list.length) setSelectedId(list[0].id);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelectedFamily(null);
      return;
    }
    const found = families.find((f) => f.id === selectedId);
    setSelectedFamily(found ? { ...found } : null);
    originalFamilyRef.current = found ? { ...found } : null;
    setUnsavedChanges(false, null);
    setNewGuestArrival(found?.arrivalDate || "");
    setNewGuestDeparture(found?.departureDate || "");
    setLinkQuery("");
    setLinkResults([]);
    setLinkLoading(false);
    setLinkError("");
  }, [selectedId, families]);

  useEffect(() => {
    if (!selectedFamily) {
      setFamilyGuests([]);
      setFamilyGuestsLoading(false);
      return;
    }
    let cancelled = false;
    async function loadFamilyGuests() {
      try {
        setFamilyGuestsLoading(true);
        const res = await fetch(`/api/families/${selectedFamily.id}/guests`);
        const json = await res.json();
        if (!cancelled) {
          setFamilyGuests(Array.isArray(json) ? json : []);
        }
      } finally {
        if (!cancelled) setFamilyGuestsLoading(false);
      }
    }
    loadFamilyGuests();
    return () => {
      cancelled = true;
    };
  }, [selectedFamily?.id]);

  const filteredFamilies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return families;
    return families.filter((f) => (f.name || "").toLowerCase().includes(q));
  }, [families, search]);

  async function saveFamily() {
    if (!selectedFamily) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/families/${selectedFamily.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: selectedFamily.name || "",
          vip: !!selectedFamily.vip,
          arrivalDate: selectedFamily.arrivalDate || null,
          departureDate: selectedFamily.departureDate || null,
          maxChoices: selectedFamily.maxChoices || null,
          infoText: selectedFamily.infoText || "",
          itineraryMessage: selectedFamily.itineraryMessage || "",
          skipDate1: selectedFamily.skipDate1 || null,
          skipDate2: selectedFamily.skipDate2 || null,
          skipDate3: selectedFamily.skipDate3 || null,
          skipSlot1: selectedFamily.skipSlot1 || null,
          skipSlot2: selectedFamily.skipSlot2 || null,
          skipSlot3: selectedFamily.skipSlot3 || null,
          skipText1: selectedFamily.skipText1 || "",
          skipText2: selectedFamily.skipText2 || "",
          skipText3: selectedFamily.skipText3 || "",
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || json?.details || "Failed to save family");
        return false;
      }
      setFamilies((prev) =>
        prev.map((f) => (f.id === selectedFamily.id ? { ...selectedFamily } : f))
      );
      originalFamilyRef.current = { ...selectedFamily };
      setUnsavedChanges(false, null);
      return true;
    } finally {
      setSaving(false);
    }
  }

  async function uploadPackingListFile(file) {
    if (!selectedFamily || !file) return;
    setUploadingPackingList(true);
    try {
      // Send as base64 JSON to avoid multipart corruption through the Vercel proxy
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch(`/api/families/${selectedFamily.id}/packing-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: file.name, mimeType: file.type || "application/octet-stream", data: base64 }),
      });
      const json = await res.json();
      if (!res.ok) { alert(json?.error || "Upload failed"); return; }
      const newAtt = { id: json.id, url: json.url, filename: json.filename };
      setSelectedFamily((prev) => ({ ...prev, packingList: [...(prev.packingList || []), newAtt] }));
    } finally {
      setUploadingPackingList(false);
    }
  }

  async function deletePackingListFile(attachmentId) {
    if (!selectedFamily) return;
    setDeletingPackingListId(attachmentId);
    try {
      const res = await fetch(`/api/families/${selectedFamily.id}/packing-list/${attachmentId}`, { method: "DELETE" });
      if (!res.ok) { alert("Delete failed"); return; }
      setSelectedFamily((prev) => ({ ...prev, packingList: (prev.packingList || []).filter((a) => a.id !== attachmentId) }));
    } finally {
      setDeletingPackingListId(null);
    }
  }

  async function createFamily() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const res = await fetch("/api/families", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || json?.details || "Failed to create family");
        return;
      }
      const newFamily = {
        id: json.id,
        name: json.name || trimmed,
        vip: false,
        arrivalDate: "",
        departureDate: "",
        maxChoices: null,
        infoText: "",
        skipDate1: "",
        skipDate2: "",
        skipDate3: "",
        skipSlot1: "",
        skipSlot2: "",
        skipSlot3: "",
        skipText1: "",
        skipText2: "",
        skipText3: "",
      };
      setFamilies((prev) => [...prev, newFamily]);
      setSelectedId(newFamily.id);
      setNewName("");
    } finally {
      setCreating(false);
    }
  }

  async function copyFamilyLink() {
    if (!selectedFamily?.id) return;
    const url = `${window.location.origin}/family/${selectedFamily.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast({ message: "Family itinerary link copied", guestId: null });
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast({ message: "Copy failed", guestId: null });
      setTimeout(() => setToast(null), 2000);
    }
  }

  async function copyFamilyChoicesLink() {
    if (!selectedFamily?.id) return;
    const url = `${window.location.origin}/family/${selectedFamily.id}/choices`;
    try {
      await navigator.clipboard.writeText(url);
      setToast({ message: "Family choices link copied", guestId: null });
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast({ message: "Copy failed", guestId: null });
      setTimeout(() => setToast(null), 2000);
    }
  }

  async function duplicateFamily() {
    if (!selectedFamily) return;
    const name = duplicateName.trim();
    if (!name) return;
    setDuplicating(true);
    try {
      const res = await fetch("/api/families/duplicate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceId: selectedFamily.id, name }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || json?.details || "Failed to duplicate family");
        return;
      }
      const cloned = {
        ...(selectedFamily || {}),
        id: json.id,
        name,
      };
      setFamilies((prev) => [...prev, cloned]);
      setSelectedId(json.id);
      setDuplicateName("");
      setDuplicateOpen(false);
    } finally {
      setDuplicating(false);
    }
  }

  useEffect(() => {
    if (!selectedFamily) {
      setLinkResults([]);
      setLinkLoading(false);
      setLinkError("");
      return;
    }
    const q = linkQuery.trim();
    if (!q) {
      setLinkResults([]);
      setLinkLoading(false);
      setLinkError("");
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setLinkLoading(true);
        const qs = new URLSearchParams();
        qs.set("search", q);
        const res = await fetch(`/api/guests?${qs.toString()}`);
        const json = await res.json();
        if (!cancelled) {
          setLinkResults(Array.isArray(json) ? json : []);
          setLinkError("");
        }
      } catch (e) {
        if (!cancelled) {
          setLinkResults([]);
          setLinkError("Unable to load guests");
        }
      } finally {
        if (!cancelled) setLinkLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [linkQuery, selectedFamily]);

  async function linkGuestToFamily(guest) {
    if (!selectedFamily || !guest?.id) return;
    try {
      const detailRes = await fetch(`/api/guests/${guest.id}`);
      const detailJson = await detailRes.json();
      if (!detailRes.ok) {
        alert(detailJson?.error || detailJson?.details || "Failed to load guest");
        return;
      }
      const existing = Array.isArray(detailJson.familyIds) ? detailJson.familyIds : [];
      const nextIds = Array.from(new Set([...existing, selectedFamily.id]));
      const res = await fetch(`/api/guests/${guest.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ familyIds: nextIds }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || json?.details || "Failed to link guest");
        return;
      }
      const familyLabel = selectedFamily?.name || "Family/Group";
      setToast({ message: `${guest.name || "Guest"} linked to ${familyLabel}`, guestId: guest.id });
      setTimeout(() => {
        setToast((prev) => (prev?.guestId === guest.id ? null : prev));
      }, 4000);
      setFamilyGuests((prev) => {
        if (prev.find((g) => g.id === guest.id)) return prev;
        return [...prev, guest];
      });
    } catch (e) {
      alert("Failed to link guest");
    }
  }

  async function removeGuestFromFamily(guest) {
    if (!selectedFamily || !guest?.id) return;
    try {
      const detailRes = await fetch(`/api/guests/${guest.id}`);
      const detailJson = await detailRes.json();
      if (!detailRes.ok) {
        alert(detailJson?.error || detailJson?.details || "Failed to load guest");
        return;
      }
      const existing = Array.isArray(detailJson.familyIds) ? detailJson.familyIds : [];
      const nextIds = existing.filter((id) => id !== selectedFamily.id);
      const res = await fetch(`/api/guests/${guest.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ familyIds: nextIds }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || json?.details || "Failed to remove guest");
        return;
      }
      const familyLabel = selectedFamily?.name || "Family/Group";
      setToast({ message: `${guest.name || "Guest"} removed from ${familyLabel}`, guestId: guest.id });
      setTimeout(() => {
        setToast((prev) => (prev?.guestId === guest.id ? null : prev));
      }, 4000);
      setFamilyGuests((prev) => prev.filter((g) => g.id !== guest.id));
    } catch (e) {
      alert("Failed to remove guest");
    }
  }

  function toggleGuestRequests(guestId) {
    setExpandedGuests((prev) => {
      const next = new Set(prev);
      if (next.has(guestId)) {
        next.delete(guestId);
      } else {
        next.add(guestId);
      }
      return next;
    });
  }

  async function loadGuestRequests(guestId) {
    if (!guestId) return;
    setGuestRequestDetails((prev) => ({
      ...prev,
      [guestId]: { loading: true, error: "", items: prev[guestId]?.items || [] },
    }));
    try {
      const [choicesRes, itinRes] = await Promise.all([
        fetch(`/api/guests/${guestId}/choices`),
        fetch(`/api/guests/${guestId}/itinerary`),
      ]);
      const choicesJson = await choicesRes.json();
      const itinJson = await itinRes.json();
      if (!choicesRes.ok) throw new Error(choicesJson?.error || choicesJson?.details || "Failed to load choices");
      if (!itinRes.ok) throw new Error(itinJson?.error || itinJson?.details || "Failed to load itinerary");

      const activityMap = new Map(
        (choicesJson.activities || []).map((a) => [a.id, a.name || "Unnamed activity"])
      );
      const scheduledIds = new Set();
      (itinJson.days || []).forEach((day) => {
        [day.am, day.pm].forEach((slot) => {
          (slot?.activityIds || []).forEach((id) => scheduledIds.add(id));
        });
      });

      const items = (choicesJson.choices || [])
        .map((id, idx) => {
          const choiceId = typeof id === "string" ? id : id?.id;
          if (!choiceId) return null;
          return {
            rank: idx + 1,
            id: choiceId,
            name: activityMap.get(choiceId) || "Unknown activity",
            scheduled: scheduledIds.has(choiceId),
          };
        })
        .filter(Boolean);

      setGuestRequestDetails((prev) => ({
        ...prev,
        [guestId]: { loading: false, error: "", items },
      }));
    } catch (e) {
      setGuestRequestDetails((prev) => ({
        ...prev,
        [guestId]: { loading: false, error: e?.message || "Failed to load", items: [] },
      }));
    }
  }

  useEffect(() => {
    if (!selectedFamily) {
      setUnsavedChanges(false, null);
      return;
    }
    const snapshot = (family) =>
      JSON.stringify({
        name: family.name || "",
        vip: !!family.vip,
        arrivalDate: family.arrivalDate || "",
        departureDate: family.departureDate || "",
        maxChoices: family.maxChoices || null,
        infoText: family.infoText || "",
        skipDate1: family.skipDate1 || "",
        skipDate2: family.skipDate2 || "",
        skipDate3: family.skipDate3 || "",
        skipSlot1: family.skipSlot1 || "",
        skipSlot2: family.skipSlot2 || "",
        skipSlot3: family.skipSlot3 || "",
        skipText1: family.skipText1 || "",
        skipText2: family.skipText2 || "",
        skipText3: family.skipText3 || "",
      });

    const original = originalFamilyRef.current ? snapshot(originalFamilyRef.current) : "";
    const current = snapshot(selectedFamily);
    const nextDirty = original && original !== current;
    setUnsavedChanges(nextDirty, saveFamily);
  }, [selectedFamily]);

  async function createGuestForFamily() {
    if (!selectedFamily) return;
    const name = newGuestName.trim();
    if (!name) return;
    if (newGuestArrival && newGuestDeparture && newGuestDeparture < newGuestArrival) {
      alert("Departure date must be after arrival date.");
      return;
    }
    setCreatingGuest(true);
    try {
      const res = await fetch("/api/guests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email: newGuestEmail.trim(),
          phone: normalizePhone(newGuestPhone),
          ...(newGuestLevel ? { level: newGuestLevel } : {}),
          arrivalDate: newGuestArrival || null,
          departureDate: newGuestDeparture || newGuestArrival || null,
          vip: false,
          familyIds: [selectedFamily.id],
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || json?.details || "Failed to create guest");
        return;
      }
      const guestId = json?.id;
      const familyLabel = selectedFamily?.name || "Family/Group";
      setToast({
        message: `${name} added to ${familyLabel}`,
        guestId,
      });
      setNewGuestName("");
      setNewGuestEmail("");
      setNewGuestPhone("");
      setNewGuestLevel("");
      setAddGuestOpen(false);
      if (guestId) {
        setTimeout(() => {
          setToast((prev) => (prev?.guestId === guestId ? null : prev));
        }, 4000);
      }
    } finally {
      setCreatingGuest(false);
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "320px minmax(0, 1fr)",
        gap: 16,
        fontFamily: "Lusitana, serif",
      }}
    >
      <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 12, background: "var(--card)" }}>
        <div style={{ fontWeight: 800 }}>Families/Groups</div>
        <input
          placeholder="Search families…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginTop: 10, width: "100%", color: "var(--ink)", fontFamily: "inherit" }}
        />
      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <input
          placeholder="New family name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ flex: 1, color: "var(--ink)", fontFamily: "inherit" }}
        />
        <button onClick={createFamily} disabled={creating || !newName.trim()} style={{ fontFamily: "inherit" }}>
          {creating ? "Adding…" : "Add"}
        </button>
      </div>

        <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
          {loading && <div style={{ opacity: 0.7 }}>Loading…</div>}
          {!loading &&
            filteredFamilies.map((f) => (
              <button
                key={f.id}
                onClick={() =>
                  requestConfirm(() => {
                    setSelectedId(f.id);
                  })
                }
                style={{
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid var(--stroke)",
                  background: selectedId === f.id ? "var(--selection-bg)" : "var(--card)",
                  color: "var(--ink)",
                  fontWeight: selectedId === f.id ? 700 : 500,
                  fontFamily: "inherit",
                }}
              >
                {f.name || "(Unnamed family)"}
              </button>
            ))}
          {!loading && filteredFamilies.length === 0 && (
            <div style={{ opacity: 0.7 }}>No families found.</div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gap: 16 }}>
        <FamilyForm
          family={selectedFamily}
          onChange={setSelectedFamily}
          onSave={saveFamily}
          saving={saving}
          onDuplicate={() => setDuplicateOpen(true)}
        />

        {selectedFamily && (
          <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 16, background: "var(--card)" }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Packing List</div>
            {(selectedFamily.packingList || []).length === 0 && (
              <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 8 }}>No files uploaded.</div>
            )}
            {(selectedFamily.packingList || []).map((att) => (
              <div key={att.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <a href={att.url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>{att.filename}</a>
                <button
                  onClick={() => deletePackingListFile(att.id)}
                  disabled={deletingPackingListId === att.id}
                  style={{ fontSize: 11 }}
                >
                  {deletingPackingListId === att.id ? "Removing…" : "Remove"}
                </button>
              </div>
            ))}
            <label style={{ display: "inline-block", marginTop: 4 }}>
              <input
                type="file"
                style={{ display: "none" }}
                disabled={uploadingPackingList}
                onChange={(e) => { if (e.target.files?.[0]) uploadPackingListFile(e.target.files[0]); e.target.value = ""; }}
              />
              <span
                style={{
                  display: "inline-block",
                  cursor: uploadingPackingList ? "default" : "pointer",
                  fontSize: 12,
                  padding: "4px 10px",
                  border: "1px solid var(--stroke)",
                  borderRadius: 6,
                  opacity: uploadingPackingList ? 0.5 : 1,
                }}
              >
                {uploadingPackingList ? "Uploading…" : "Upload File"}
              </span>
            </label>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={copyFamilyLink} disabled={!selectedFamily} style={{ fontSize: 12 }}>
              Copy Family Itinerary Link
            </button>
            <button onClick={copyFamilyChoicesLink} disabled={!selectedFamily} style={{ fontSize: 12 }}>
              Copy Family Choices Link
            </button>
          </div>
          <button onClick={() => setAddGuestOpen(true)} disabled={!selectedFamily}>
            Add Guest
          </button>
        </div>

        <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
          <div style={{ fontWeight: 800 }}>Link Existing Guests</div>
          <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
            <input
              placeholder="Search guests…"
              value={linkQuery}
              onChange={(e) => setLinkQuery(e.target.value)}
              disabled={!selectedFamily}
            />
            {linkLoading && <div style={{ fontSize: 12, opacity: 0.7 }}>Searching…</div>}
            {linkError ? <div style={{ fontSize: 12, color: "crimson" }}>{linkError}</div> : null}
            {!linkLoading && linkQuery.trim() && linkResults.length === 0 ? (
              <div style={{ fontSize: 12, opacity: 0.7 }}>No guests found.</div>
            ) : null}
            <div style={{ display: "grid", gap: 6 }}>
              {linkResults
                .filter((g) => !familyGuests.some((fg) => fg.id === g.id))
                .map((g) => (
                  <div
                    key={g.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "6px 8px",
                      borderRadius: 8,
                      border: "1px solid var(--stroke)",
                    }}
                  >
                    <div style={{ fontSize: 13 }}>{g.name || "Unnamed guest"}</div>
                    <button onClick={() => linkGuestToFamily(g)} style={{ fontSize: 12 }}>
                      Add
                    </button>
                  </div>
                ))}
            </div>
            {selectedFamily ? (
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                Guests will be linked to {selectedFamily.name || "this family"}.
              </div>
            ) : (
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                Select a family to link guests.
              </div>
            )}
          </div>
        </div>

        <details style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 12, background: "var(--card)" }} open>
          <summary style={{ cursor: "pointer" }}>
            Family Guests ({familyGuests.length})
          </summary>
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {familyGuestsLoading && <div style={{ fontSize: 12, opacity: 0.7 }}>Loading guests…</div>}
            {!familyGuestsLoading && familyGuests.length === 0 && (
              <div style={{ fontSize: 12, opacity: 0.7 }}>No guests linked.</div>
            )}
            {!familyGuestsLoading &&
              familyGuests.map((g) => {
                const isExpanded = expandedGuests.has(g.id);
                const details = guestRequestDetails[g.id];
                return (
                <div
                  key={g.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 8,
                    alignItems: "center",
                    border: "1px solid var(--stroke)",
                    borderRadius: 8,
                    padding: "6px 8px",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{g.name || "Unnamed guest"}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      {g.arrivalDate ? `Arrive ${g.arrivalDate}` : ""}
                      {g.arrivalDate && g.departureDate ? " • " : ""}
                      {g.departureDate ? `Depart ${g.departureDate}` : ""}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                      {Number.isFinite(g.requestedCount)
                        ? `${g.requestedCount}/${selectedFamily?.maxChoices || 5} items requested`
                        : `0/${selectedFamily?.maxChoices || 5} items requested`}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <button
                        onClick={() => {
                          toggleGuestRequests(g.id);
                          if (!guestRequestDetails[g.id]) loadGuestRequests(g.id);
                        }}
                        style={{ fontSize: 11 }}
                      >
                        {isExpanded ? "Hide requests" : "View requests"}
                      </button>
                    </div>
                    {isExpanded && (
                      <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                        {details?.loading && (
                          <div style={{ fontSize: 12, opacity: 0.7 }}>Loading requests…</div>
                        )}
                        {details?.error ? (
                          <div style={{ fontSize: 12, color: "crimson" }}>{details.error}</div>
                        ) : null}
                        {!details?.loading && details?.items?.length === 0 ? (
                          <div style={{ fontSize: 12, opacity: 0.7 }}>No requests submitted.</div>
                        ) : null}
                        {!details?.loading &&
                          details?.items?.map((item) => (
                            <div
                              key={`${g.id}-${item.id}-${item.rank}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                                padding: "4px 6px",
                                borderRadius: 6,
                                border: "1px solid var(--stroke)",
                              }}
                            >
                              <div style={{ fontSize: 12 }}>
                                {item.rank}. {item.name}
                              </div>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: item.scheduled ? "#1b7f4a" : "#8a3b12",
                                }}
                              >
                                {item.scheduled ? "Scheduled" : "Not scheduled"}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeGuestFromFamily(g)} style={{ fontSize: 12 }}>
                    Remove
                  </button>
                </div>
              );
              })}
          </div>
        </details>
      </div>

      {addGuestOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => !creatingGuest && setAddGuestOpen(false)}
        >
          <div
            style={{
              background: "var(--card)",
              borderRadius: 12,
              padding: 16,
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              display: "grid",
              gap: 12,
              border: "1px solid var(--stroke)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Add Guest</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {selectedFamily?.name || "Family"}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 700 }}>Name</div>
                <input
                  value={newGuestName}
                  onChange={(e) => setNewGuestName(e.target.value)}
                  placeholder="Guest name"
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 700 }}>Email</div>
                <input
                  value={newGuestEmail}
                  onChange={(e) => setNewGuestEmail(e.target.value)}
                  placeholder="name@email.com"
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 700 }}>Phone</div>
                <input
                  value={newGuestPhone}
                  onChange={(e) => setNewGuestPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 700 }}>Horseback Riding Level</div>
                <select
                  value={newGuestLevel}
                  onChange={(e) => setNewGuestLevel(e.target.value)}
                >
                  <option value="">Select level…</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontWeight: 700 }}>Arrival Date</div>
              <input
                type="date"
                value={newGuestArrival}
                onChange={(e) => setNewGuestArrival(e.target.value)}
                onBlur={() => {
                  if (!newGuestDeparture && newGuestArrival) {
                    setNewGuestDeparture(newGuestArrival);
                  }
                }}
              />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontWeight: 700 }}>Departure Date</div>
              <input
                type="date"
                value={newGuestDeparture}
                onChange={(e) => setNewGuestDeparture(e.target.value)}
              />
            </div>
          </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setAddGuestOpen(false)} disabled={creatingGuest}>
                Cancel
              </button>
              <button onClick={createGuestForFamily} disabled={creatingGuest || !newGuestName.trim()}>
                {creatingGuest ? "Saving…" : "Add Guest"}
              </button>
            </div>
          </div>
        </div>
      )}

      {duplicateOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => !duplicating && setDuplicateOpen(false)}
        >
          <div
            style={{
              background: "var(--card)",
              borderRadius: 12,
              padding: 16,
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              display: "grid",
              gap: 12,
              border: "1px solid var(--stroke)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: 16 }}>Duplicate Family/Group</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              This will copy all settings from {selectedFamily?.name || "the selected family"}.
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontWeight: 700 }}>New Family Name</div>
              <input
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Family name"
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDuplicateOpen(false)} disabled={duplicating}>
                Cancel
              </button>
              <button onClick={duplicateFamily} disabled={duplicating || !duplicateName.trim()}>
                {duplicating ? "Saving…" : "Duplicate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: "fixed",
            top: 16,
            right: 20,
            background: "var(--card)",
            color: "var(--ink)",
            border: "1px solid var(--stroke)",
            borderRadius: 12,
            padding: "10px 12px",
            boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
            display: "flex",
            gap: 10,
            alignItems: "center",
            zIndex: 1200,
            maxWidth: 360,
          }}
        >
          <div style={{ fontSize: 13 }}>{toast.message}</div>
          {toast.guestId ? (
            <a
              href={`/guests?guestId=${encodeURIComponent(toast.guestId)}`}
              style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)", textDecoration: "none" }}
            >
              View
            </a>
          ) : null}
          <button onClick={() => setToast(null)} style={{ fontSize: 11 }}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}
